/**
 * Helpers for rendering the per-position Income Risk badge that comes
 * back from `GET /api/portfolio/risk-score`. Kept lightweight so any
 * surface (Portfolio rows, Stock modal, Sector view) can reuse the
 * same color + label vocabulary.
 *
 * `computeRisk(stock)` mirrors the backend `_compute_ticker_risk` in
 * `server.py` so the Stock Modal can render the same badge without
 * an extra round-trip — the inputs are all fields already present on
 * `stock_cache` rows the modal already has.
 */

export function computeRisk(stock) {
  if (!stock) return null;
  let score = 5;
  const reasons = [];

  const tier = (stock.dividend_tier || '').toLowerCase();
  if (tier === 'king') {
    score -= 3;
    reasons.push('Dividend King (50+ yr streak)');
  } else if (tier === 'aristocrat') {
    score -= 2.5;
    reasons.push('Dividend Aristocrat (25+ yr streak)');
  } else if (tier === 'achiever') {
    score -= 1.5;
    reasons.push('Dividend Achiever (10+ yr streak)');
  }

  const pr = Number(stock.payout_ratio);
  if (Number.isFinite(pr) && pr > 0) {
    if (pr < 40) {
      score -= 1;
      reasons.push(`Low payout ratio (${pr.toFixed(0)}%)`);
    } else if (pr < 60) {
      // neutral
    } else if (pr < 80) {
      score += 0.5;
    } else if (pr < 100) {
      score += 1.5;
      reasons.push(`High payout ratio (${pr.toFixed(0)}%)`);
    } else if (pr < 150) {
      score += 2.5;
      reasons.push(`Stretched payout ratio (${pr.toFixed(0)}%)`);
    } else {
      score += 3.5;
      reasons.push(`Unsustainable payout ratio (${pr.toFixed(0)}%)`);
    }
  }

  const dy = Number(stock.dividend_yield);
  if (Number.isFinite(dy) && dy > 0) {
    if (dy < 3) score -= 0.5;
    else if (dy < 6) {
      // neutral
    } else if (dy < 10) {
      score += 1;
      reasons.push(`Elevated yield (${dy.toFixed(1)}%)`);
    } else if (dy < 15) {
      score += 2;
      reasons.push(`Very high yield (${dy.toFixed(1)}%)`);
    } else {
      score += 3;
      reasons.push(`Yield-trap warning (${dy.toFixed(1)}%)`);
    }
  }

  if (stock.is_leveraged) {
    score += 2.5;
    reasons.push('Leveraged fund');
  } else if (stock.uses_futures) {
    score += 1.5;
    reasons.push('Uses futures / derivatives');
  }

  if (stock.is_cef) {
    score += 0.5;
    reasons.push('Closed-End Fund (NAV / leverage risk)');
  }

  if (stock.is_bdc) {
    score += 0.5;
    reasons.push('BDC (regulated leverage)');
  }

  const final = Math.max(1, Math.min(10, Math.round(score)));
  let category = 'moderate';
  let color = 'amber';
  if (final <= 3) { category = 'safe'; color = 'emerald'; }
  else if (final >= 7) { category = 'risky'; color = 'rose'; }

  return { score: final, category, color, reasons: reasons.slice(0, 4) };
}

// Map a 1..10 score → tailwind classes for the dot + tooltip text.
// Mirrors the backend `_compute_ticker_risk` thresholds.
export function riskColorClasses(category) {
  switch (category) {
    case 'safe':
      return {
        dot: 'bg-emerald-500',
        ring: 'ring-emerald-500/40',
        text: 'text-emerald-700 dark:text-emerald-300',
      };
    case 'risky':
      return {
        dot: 'bg-rose-500',
        ring: 'ring-rose-500/40',
        text: 'text-rose-700 dark:text-rose-300',
      };
    case 'moderate':
    default:
      return {
        dot: 'bg-amber-500',
        ring: 'ring-amber-500/40',
        text: 'text-amber-700 dark:text-amber-300',
      };
  }
}

export function riskLabel(category) {
  if (category === 'safe') return 'Safe';
  if (category === 'risky') return 'Risky';
  return 'Moderate';
}

/**
 * Build a tooltip-ready string explaining the score.
 * Example: "Score 4/10 — Moderate · Reasons: Dividend Achiever, Elevated yield (7.2%)"
 */
export function riskTooltip(risk) {
  if (!risk) return '';
  const reasons = (risk.reasons || []).join(' · ') || 'No notable risk flags';
  return `Score ${risk.score}/10 — ${riskLabel(risk.category)}\n${reasons}`;
}
